import React from 'react';
import moment from 'moment';
import './CalendarWidget.scss';
import { withCustomEvent } from '../hoc/withCustomEvent';

interface HeadingProps {
  date: moment.Moment;
  changeMonth: (num: any) => any;
  resetDate: () => any;
}

const Heading = ({ date, changeMonth, resetDate }: HeadingProps) => (
  <nav className="calendar__nav">
    <a onClick={() => changeMonth(date.month() - 1)}>&#8249;</a>
    <h1 onClick={() => resetDate()}>
      {date.format('MMMM')} <small>{date.format('YYYY')}</small>
    </h1>
    <a onClick={() => changeMonth(date.month() + 1)}>&#8250;</a>
  </nav>
);

interface DayProps {
  currentDate: moment.Moment;
  date: moment.Moment;
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  onClick: (date: moment.Moment) => any;
}
const Day = ({ currentDate, date, startDate, endDate, onClick }: DayProps) => {
  let className = [];

  if (moment().isSame(date, 'day')) {
    className.push('active');
  }

  if (date.isSame(startDate, 'day')) {
    className.push('start');
  }

  if (date.isBetween(startDate, endDate, 'day')) {
    className.push('between');
  }

  if (date.isSame(endDate, 'day')) {
    className.push('end');
  }

  if (!date.isSame(currentDate, 'month')) {
    className.push('muted');
  }

  return (
    <span onClick={() => onClick(date)} className={className.join(' ')}>
      {date.date()}
    </span>
  );
};

interface DaysProps {
  date: moment.Moment;
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  onClick: (date: moment.Moment) => any;
}

const Days = ({ date, startDate, endDate, onClick }: DaysProps) => {
  const thisDate = moment(date);
  const daysInMonth = moment(date).daysInMonth();
  const firstDayDate = moment(date).startOf('month');
  const previousMonth = moment(date).subtract(1, 'month');
  const previousMonthDays = previousMonth.daysInMonth();
  const nextsMonth = moment(date).add(1, 'month');
  let days = [];
  let labels = [];

  for (let i = 1; i <= 7; i++) {
    labels.push(
      <span className="label">
        {moment()
          .day(i)
          .format('ddd')}
      </span>
    );
  }

  for (let i = firstDayDate.day(); i > 1; i--) {
    previousMonth.date(previousMonthDays - i + 2);

    days.push(
      <Day
        key={moment(previousMonth).format('DD MM YYYY')}
        onClick={(date: any) => onClick(date)}
        currentDate={date}
        date={moment(previousMonth)}
        startDate={startDate}
        endDate={endDate}
      />
    );
  }

  for (let i = 1; i <= daysInMonth; i++) {
    thisDate.date(i);

    days.push(
      <Day
        key={moment(thisDate).format('DD MM YYYY')}
        onClick={(date: any) => onClick(date)}
        currentDate={date}
        date={moment(thisDate)}
        startDate={startDate}
        endDate={endDate}
      />
    );
  }

  const daysCount = days.length;
  for (let i = 1; i <= 42 - daysCount; i++) {
    nextsMonth.date(i);
    days.push(
      <Day
        key={`${nextsMonth}${i}`}
        onClick={(date: any) => onClick(date)}
        currentDate={date}
        date={moment(nextsMonth)}
        startDate={startDate}
        endDate={endDate}
      />
    );
  }

  return (
    <nav className="calendar__days">
      {labels.concat()}
      {days.concat()}
    </nav>
  );
};

const CalendarWidgetBase: React.FC<any> = ({ onEmit }) => {
  const [date, setDate] = React.useState<moment.Moment>(moment());
  const [startDate, setStartDate] = React.useState<moment.Moment | null>(null);
  const [endDate, setEndDate] = React.useState<moment.Moment | null>(null);

  const resetDate = () => {
    setDate(moment());
  };

  const changeMonth = (month: string | number) => {
    const newDate = moment(date).month(month);
    setDate(newDate);
  };

  const changeDate = (date: moment.Moment) => {
    let chStartDate: moment.Moment | null = moment(startDate);
    let chEndDate: moment.Moment | null = moment(endDate);
    if (
      chStartDate === null ||
      date.isBefore(chStartDate, 'day') ||
      !chStartDate.isSame(chEndDate, 'day')
    ) {
      chStartDate = moment(date);
      chEndDate = moment(date);
    } else if (
      date.isSame(chStartDate, 'day') &&
      date.isSame(chEndDate, 'day')
    ) {
      chStartDate = null;
      chEndDate = null;
    } else if (date.isAfter(chStartDate, 'day')) {
      chEndDate = moment(date);
    }

    setStartDate(chStartDate);
    setEndDate(chEndDate);
  };

  return (
    <div className="calendar">
      <Heading
        date={date}
        changeMonth={(month: any) => changeMonth(month)}
        resetDate={() => resetDate()}
      />

      <Days
        onClick={(date: any) => changeDate(date)}
        date={date}
        startDate={startDate}
        endDate={endDate}
      />
      <div className="calendar__action">
        <button
          className="calendar__action__btn"
          type="button"
          onClick={e => {
            if (onEmit) {
              onEmit(e, {
                name: 'date-range',
                detail: {
                  startDate: startDate?.toDate(),
                  endDate: endDate?.toDate(),
                },
              });
            }
          }}
        >
          Select
        </button>
      </div>
    </div>
  );
};

export const CalendarWidget = withCustomEvent(CalendarWidgetBase);
